// frontend/src/components/conversion/conversionPopupMemory.js
//
// The popup's frequency memory - the ONLY reader and writer of its two keys.
//
//   sessionStorage `lf_cpop_shown` = "1"     set at impression: once per session
//                                            (a step-1 submit is covered by it -
//                                            nothing shows again this session).
//   localStorage   `lf_cpop_quiet_until` = ms set on an explicit close (X / Esc):
//                                            no popup for 7 days on this browser.
//
// Neither key holds anything about the visitor - a flag and a timestamp. Every
// access is guarded: storage throws in some private modes and when site data
// is blocked, and then the popup may still show, just without memory (owner
// brief) - a blocked read is "nothing remembered", never a crash.

export const SHOWN_SESSION_KEY = "lf_cpop_shown";
export const QUIET_UNTIL_KEY = "lf_cpop_quiet_until";

/** The quiet period after an explicit close. */
export const QUIET_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Was the popup already shown in this tab's session?
 *
 * @returns {boolean} false when unknown (storage blocked).
 */
export function wasShownThisSession() {
  try {
    return window.sessionStorage.getItem(SHOWN_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember the impression for the rest of this session. Never throws. */
export function markShownThisSession() {
  try {
    window.sessionStorage.setItem(SHOWN_SESSION_KEY, "1");
  } catch {
    /* blocked storage - the popup simply has no memory */
  }
}

/**
 * Is the post-close quiet period still running?
 *
 * A value that is not a finite number is ignored (treated as no quiet period)
 * rather than trusted.
 *
 * @param {number} [now=Date.now()]
 * @returns {boolean}
 */
export function isInQuietPeriod(now = Date.now()) {
  try {
    const raw = window.localStorage.getItem(QUIET_UNTIL_KEY);
    if (raw === null) return false;
    const until = Number(raw);
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
}

/**
 * Start the 7-day quiet period (an explicit close). Never throws.
 *
 * @param {number} [now=Date.now()]
 */
export function startQuietPeriod(now = Date.now()) {
  try {
    window.localStorage.setItem(QUIET_UNTIL_KEY, String(now + QUIET_PERIOD_MS));
  } catch {
    /* blocked storage - the popup simply has no memory */
  }
}

/**
 * May the frequency rules let the popup show now?
 *
 * @returns {boolean}
 */
export function frequencyAllowsShow() {
  return !wasShownThisSession() && !isInQuietPeriod();
}
